import { useToastState } from "@react-stately/toast";
import { useA11yMessage } from "containers/A11yStatusProvider/A11yStatusContext";
import { A11yStatusUpdate } from "core/types";
import {
  NotificationContentType,
  ToastContentType,
  ToastNotificationRegion,
} from "ds/ui";
import { ReactNode, createContext, useCallback, useContext } from "react";
import { useNotificationState } from "./NotificationReducer";

const TOAST_TIMEOUT = 5000;

// `danger` toasts/notifications are time-sensitive errors that warrant an
// assertive announcement; everything else (incl. `success`) is announced
// politely so it does not interrupt the screen reader.
function a11yRoleForColor(color: string): A11yStatusUpdate["role"] {
  return color === "danger" ? "alert" : "status";
}

const defaultNotificationContext = { notify: () => {}, toast: () => {} };
const NotificationContext = createContext<{
  notify: (options: NotificationContentType) => void;
  toast: (options: ToastContentType) => void;
}>({ notify: () => {}, toast: () => {} });

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context || context === defaultNotificationContext) {
    throw new Error(
      "useNotification must be used within a ToastNotificationProvider",
    );
  }
  return context.notify;
}

export function useToastContext() {
  const context = useContext(NotificationContext);
  if (!context || context === defaultNotificationContext) {
    throw new Error(
      "useToastContext must be used within a ToastNotificationProvider",
    );
  }
  return context.toast;
}

export function useToast() {
  const toast = useToastContext();

  return (options: ToastContentType) => toast(options);
}

const MAX_VISIBLE_NOTIFICATIONS = 3;
const MAX_VISIBLE_TOASTS = 5;

export function ToastNotificationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { updateA11yMessage } = useA11yMessage();
  const notificationState = useNotificationState({
    maxVisibleToasts: MAX_VISIBLE_NOTIFICATIONS,
  });
  const toastState = useToastState<ToastContentType>({
    maxVisibleToasts: MAX_VISIBLE_TOASTS,
  });

  const showToast = useCallback(
    (payload: ToastContentType) => {
      toastState.add(payload, { timeout: TOAST_TIMEOUT });
      // The toast region is unmounted while the queue is empty, so its own
      // ARIA roles are unreliable. Announce through the always-mounted app
      // live region so screen readers reliably read the toast.
      updateA11yMessage(payload.message, a11yRoleForColor(payload.color));
    },
    [toastState.add, updateA11yMessage],
  );

  const showNotification = useCallback(
    (payload: NotificationContentType) => {
      notificationState.add(payload);
      const announcement = [payload.title, payload.message]
        .filter(Boolean)
        .join(". ");
      updateA11yMessage(announcement, a11yRoleForColor(payload.color));
    },
    [notificationState.add, updateA11yMessage],
  );

  const haveNotifications =
    notificationState.visibleToasts.length > 0 ||
    toastState.visibleToasts.length > 0;

  return (
    <NotificationContext.Provider
      value={{ notify: showNotification, toast: showToast }}
    >
      {children}
      {haveNotifications && (
        <ToastNotificationRegion
          notificationState={notificationState}
          toastState={toastState}
        />
      )}
    </NotificationContext.Provider>
  );
}
